"use client";

import React from "react";
import { Building2, House as Home, Hammer, ShieldCheck, ArrowRight, MapPin, CircleCheck as CheckCircle2, HardHat, Layers, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const residentialPoints = ["Roof replacements, tile repairs & solar installations", "Single & multi-storey home extensions", "Chimney stack repairs, repointing & lead work", "Exterior rendering, painting & masonry access", "Guttering, soffit & fascia replacements"];
const commercialPoints = ["Town-centre commercial & mixed-use properties", "Office developments & corporate refurbishments", "Live, occupied site access management & public protection", "Main contractor & developer site packages", "Licensed pavement scaffolding & pedestrian gantries"];
const tradeAccessItems = [{
  title: "Roofing Scaffolding",
  description: "Dedicated roof-line access, chimney cradles, and loading bays for roofing specialists.",
  to: "/RoofingScaffoldingPage",
  icon: Hammer
}, {
  title: "Temporary Roofs",
  description: "Full weather protection and shrink-wrap canopies for sensitive structural builds.",
  to: "/TemporaryRoofsPage",
  icon: Layers
}, {
  title: "Edge Protection",
  description: "Guardrails, debris netting, and safety handrails ensuring full NASC/CHAS compliance.",
  to: "/EdgeProtectionPage",
  icon: ShieldCheck
}];
function AscotCoverageContent() {
  const {
    posts,
    loading
  } = usePosts();
  const prefersReducedMotion = useReducedMotion();
  const residentialPhoto = posts.find(p => p.customFields?.photo_name === "Ascot Residential Scaffolding Image" || p.title === "Ascot Residential Scaffolding Image");
  const residentialSrc = residentialPhoto?.customFields?.image || residentialPhoto?.featuredImage;
  const residentialAlt = residentialPhoto?.customFields?.alt_text || "Residential scaffolding on a brick property in Ascot";
  const commercialPhoto = posts.find(p => p.customFields?.photo_name === "Ascot Commercial Scaffolding Image" || p.title === "Ascot Commercial Scaffolding Image");
  const commercialSrc = commercialPhoto?.customFields?.image || commercialPhoto?.featuredImage;
  const commercialAlt = commercialPhoto?.customFields?.alt_text || "Commercial scaffolding erected for office refurbishment in Ascot";
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  return <section id="ascotscaffoldingcoverage" data-nav="light" className="bg-background text-foreground relative py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <MapPin className="size-3.5 text-primary" />
            <span>Ascot & Berkshire Coverage</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Scaffolding access across Ascot
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            From High Street commercial refurbishments and office developments to private residential extensions and historic properties across the SL5 postcode district, we supply safe, dependable scaffolding designed around your project schedule.
          </p>
        </div>

        {/* Two Main Subsections: Residential & Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Residential Card */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="flex flex-col">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col justify-between overflow-hidden">
              <div>
                <div className="relative h-56 w-full overflow-hidden bg-muted">
                  {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={residentialSrc || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffolding_ascot.png"} data-wvc-srcset={residentialSrc ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffolding_ascot.png-wvc-srcset"} data-wvc-sizes={residentialSrc ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffolding_ascot.png-wvc-sizes"} alt={residentialAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />}
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="shadow-xs backdrop-blur-xs font-medium">
                      <Home className="size-3 mr-1 text-primary" /> Residential
                    </Badge>
                  </div>
                </div>

                <CardHeader className="pt-6">
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    Residential scaffolding in Ascot
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-base mt-2">
                    Careful, prompt domestic scaffolding for homeowners, self-builders, and local trades working on residential developments, extensions, and renovations.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 pb-6">
                  {residentialPoints.map((point, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                      <span className="text-sm text-foreground/90">{point}</span>
                    </div>)}
                </CardContent>
              </div>

              <div className="p-6 pt-0 mt-auto border-t border-border/60 bg-muted/20">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Explore Residential Services
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" asChild className="group">
                    <Link to="/ResidentialScaffoldingPage">
                      Residential Scaffolding
                      <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild className="group">
                    <Link to="/ChimneyScaffoldingPage">
                      Chimney Scaffolding
                      <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Commercial Card */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="flex flex-col">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col justify-between overflow-hidden">
              <div>
                <div className="relative h-56 w-full overflow-hidden bg-muted">
                  {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={commercialSrc || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_ascot.png"} data-wvc-srcset={commercialSrc ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_ascot.png-wvc-srcset"} data-wvc-sizes={commercialSrc ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_ascot.png-wvc-sizes"} alt={commercialAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />}
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="shadow-xs backdrop-blur-xs font-medium">
                      <Building2 className="size-3 mr-1 text-primary" /> Commercial
                    </Badge>
                  </div>
                </div>

                <CardHeader className="pt-6">
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    Commercial scaffolding in Ascot
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-base mt-2">
                    Engineered access solutions for main contractors, commercial property managers, retail fit-outs, and mixed-use premises throughout Ascot.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 pb-6">
                  {commercialPoints.map((point, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                      <span className="text-sm text-foreground/90">{point}</span>
                    </div>)}
                </CardContent>
              </div>

              <div className="p-6 pt-0 mt-auto border-t border-border/60 bg-muted/20">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Explore Commercial Services
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" asChild className="group">
                    <Link to="/CommercialScaffoldingPage">
                      Commercial Scaffolding
                      <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild className="group">
                    <Link to="/MainContractorsDevelopersPage">
                      Main Contractors & Developers
                      <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <motion.div initial={{
        opacity: 0,
        y: prefersReducedMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-card p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                <HardHat className="size-4" />
                <span>Specialist Contractor Support</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground font-default">
                Roofing & trade access in Ascot
              </h3>
              <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">
                Purpose-built platforms ensuring safe working heights, efficient loading zones, and total weather protection for roofing contractors, solar installers, and finishing trades.
              </p>
            </div>
            <Button asChild className="shrink-0">
              <Link to="/ContactRequestQuotePage">
                Request a Site Survey
                <ChevronRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <Separator className="my-6" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tradeAccessItems.map((item, index) => {
            const IconComp = item.icon;
            return <div key={item.title} data-index={index} className="group rounded-xl border border-border/80 bg-background p-6 transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-md flex flex-col justify-between">
                  <div>
                    <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                      <IconComp className="size-5" />
                    </div>
                    <h4 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-border/60">
                    <Link to={item.to} className="inline-flex items-center text-sm font-medium text-primary hover:underline underline-offset-4">
                      View details
                      <ChevronRight className="size-4 ml-0.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Nearby Areas Coverage */}
        <div className="mt-12 pt-10 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
              <MapPin className="size-4 text-primary" />
              <span>Nearby Berkshire areas covered</span>
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Providing rapid site surveys and compliant access across neighbouring locations:
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm">
              <Link to="#" className="font-medium text-foreground hover:text-primary transition-colors inline-flex items-center gap-1">
                <span>Scaffolding in Bracknell</span>
                <ArrowRight className="size-3 text-muted-foreground" />
              </Link>
              <span className="text-border hidden sm:inline">•</span>
              <Link to="#" className="font-medium text-foreground hover:text-primary transition-colors inline-flex items-center gap-1">
                <span>Scaffolding in Windsor</span>
                <ArrowRight className="size-3 text-muted-foreground" />
              </Link>
              <span className="text-border hidden sm:inline">•</span>
              <Link to="#" className="font-medium text-foreground hover:text-primary transition-colors inline-flex items-center gap-1">
                <span>Scaffolding in Maidenhead</span>
                <ArrowRight className="size-3 text-muted-foreground" />
              </Link>
            </div>
          </div>
          <div className="shrink-0">
            <Button variant="outline" size="sm" asChild className="group">
              <Link to="#">
                View all areas
                <ChevronRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
export default function AscotCoverage() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <AscotCoverageContent />
    </PostsProvider>;
}